import { supabase } from './supabase'
import { logActivity } from './activityService'

// Request deposit
export const requestDeposit = async (userId, amount, description = '') => {
  try {
    // Get current balance
    const { data: user } = await supabase
      .from('users')
      .select('balance')
      .eq('id', userId)
      .single()

    const { data, error } = await supabase
      .from('transactions')
      .insert({
        user_id: userId,
        type: 'deposit',
        amount: parseFloat(amount),
        balance_before: user.balance,
        balance_after: user.balance, // Will be updated on approval
        description: description || 'Deposit request',
        status: 'pending'
      })
      .select()
      .single()

    if (error) throw error

    await logActivity(userId, 'deposit_request', `Requested deposit of $${amount}`)

    return { data, error: null }
  } catch (error) {
    return { data: null, error: error.message }
  }
}

// Request withdrawal
export const requestWithdrawal = async (userId, amount, description = '') => {
  try {
    // Get current balance
    const { data: user } = await supabase
      .from('users')
      .select('balance')
      .eq('id', userId)
      .single()

    if (user.balance < amount) {
      throw new Error('Insufficient funds')
    }

    const { data, error } = await supabase
      .from('transactions')
      .insert({
        user_id: userId,
        type: 'withdrawal',
        amount: parseFloat(amount),
        balance_before: user.balance,
        balance_after: user.balance, // Will be updated on approval
        description: description || 'Withdrawal request',
        status: 'pending'
      })
      .select()
      .single()

    if (error) throw error

    await logActivity(userId, 'withdrawal_request', `Requested withdrawal of $${amount}`)

    return { data, error: null }
  } catch (error) {
    return { data: null, error: error.message }
  }
}

// Get user transactions
export const getUserTransactions = async (userId, limit = 50, filters = {}) => {
  try {
    let query = supabase
      .from('transactions')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit)

    // Apply filters
    if (filters.type) {
      query = query.eq('type', filters.type)
    }
    if (filters.status) {
      query = query.eq('status', filters.status)
    }
    if (filters.startDate) {
      query = query.gte('created_at', filters.startDate)
    }
    if (filters.endDate) {
      query = query.lte('created_at', filters.endDate)
    }

    const { data, error } = await query

    if (error) throw error

    return { data, error: null }
  } catch (error) {
    return { data: null, error: error.message }
  }
}

// Get transaction by ID
export const getTransactionById = async (transactionId) => {
  try {
    const { data, error } = await supabase
      .from('transactions')
      .select(`
        *,
        users (username, email, full_name)
      `)
      .eq('id', transactionId)
      .single()

    if (error) throw error

    return { data, error: null }
  } catch (error) {
    return { data: null, error: error.message }
  }
}

// Get pending transactions count
export const getPendingTransactionsCount = async (userId) => {
  try {
    const { count, error } = await supabase
      .from('transactions')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('status', 'pending')

    if (error) throw error

    return { count, error: null }
  } catch (error) {
    return { count: 0, error: error.message }
  }
}

// Get transaction statistics
export const getTransactionStats = async (userId, days = 30) => {
  try {
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - days)

    const { data, error } = await supabase
      .from('transactions')
      .select('type, amount, status, created_at')
      .eq('user_id', userId)
      .gte('created_at', startDate.toISOString())

    if (error) throw error

    // Calculate stats
    const stats = {
      totalDeposits: 0,
      totalWithdrawals: 0,
      totalTransactions: data.length,
      pendingCount: 0,
      approvedCount: 0,
      rejectedCount: 0
    }

    data.forEach(transaction => {
      if (transaction.status === 'pending') stats.pendingCount++
      if (transaction.status === 'approved') stats.approvedCount++
      if (transaction.status === 'rejected') stats.rejectedCount++

      if (transaction.type === 'deposit' && transaction.status === 'approved') {
        stats.totalDeposits += transaction.amount
      }
      if (transaction.type === 'withdrawal' && transaction.status === 'approved') {
        stats.totalWithdrawals += transaction.amount
      }
    })

    return { data: stats, error: null }
  } catch (error) {
    return { data: null, error: error.message }
  }
}

// Export transactions to CSV
export const exportTransactionsToCSV = (transactions) => {
  const headers = ['Date', 'Type', 'Amount', 'Balance Before', 'Balance After', 'Status', 'Description']
  const rows = transactions.map(t => [
    new Date(t.created_at).toLocaleString(),
    t.type,
    t.amount,
    t.balance_before,
    t.balance_after,
    t.status,
    t.description || ''
  ])

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `transactions_${new Date().toISOString().split('T')[0]}.csv`
  a.click()
  window.URL.revokeObjectURL(url)
}

export default {
  requestDeposit,
  requestWithdrawal,
  getUserTransactions,
  getTransactionById,
  getPendingTransactionsCount,
  getTransactionStats,
  exportTransactionsToCSV
}